import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import {
  CONTENT_PAGE_EXISTS_QUERY,
  SanityService,
  contentPageExistsStateKey,
} from '@app/core/sanity';
import { CANONICAL_CONTENT_SLUGS } from '@app/core/routing';

/**
 * @description
 * `CanActivateFn` for the dynamic `page/:slug` route (design spec §4): resolves the slug against
 * Sanity *before* the renderer activates, so a missing document redirects via a `UrlTree` — the
 * mechanism SSR (`@angular/ssr`) converts into a genuine HTTP redirect, unlike an imperative
 * `Router.navigateByUrl()` fired later from inside the component (which only ever produces a 200
 * over SSR; see `ContentPageRendererComponent`'s own client-side fallback for that path).
 *
 * Deliberately fetches a lightweight `defined(...)` existence check under its OWN
 * `TransferState` key (`contentPageExistsStateKey`), distinct from the full-document key
 * `loadContentPage` (`content-page.model.ts`) reads on the renderer. `SanityService.fetch`
 * REMOVES its transfer-state entry on read, so if the guard reused the renderer's key it would
 * drain the renderer's cached document before the renderer ever ran, forcing a client re-fetch
 * and a loading-skeleton flash over already-hydrated content. Using a separate key means the
 * guard's own SSR-populated entry is consumed here, while the renderer's entry survives intact
 * for the renderer to consume.
 *
 * Not applied to the explicit `terms-and-conditions` / `privacy-policy` routes — those must
 * render the renderer's own "this page is being updated" fallback on a missing document, not 404.
 */
export const contentPageExistsGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const sanityService = inject(SanityService);
  const slug = route.paramMap.get('slug') ?? '';

  // F6: the six migrated pages are also reachable under their own explicit route — redirect
  // /page/<slug> to the canonical path instead of rendering a duplicate at both URLs.
  const canonicalPath = CANONICAL_CONTENT_SLUGS[slug];
  if (canonicalPath) {
    return of(router.parseUrl(canonicalPath));
  }

  return sanityService
    .fetch<boolean | null>(CONTENT_PAGE_EXISTS_QUERY, contentPageExistsStateKey(slug), undefined, {
      slug,
    })
    .pipe(
      map((exists) => (exists ? true : router.parseUrl('/404'))),
      catchError(() => of(router.parseUrl('/404')))
    );
};
